import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import AuthFormLayout from './common/AuthFormLayout';

const fadeIn = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const AuthScreen = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/auth/google', {
        token: credentialResponse.credential,
      });
      if (response.data.access_token) {
        onLogin(response.data.access_token, response.data.user);
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === 'waitlist_pending') {
        setError(
          'You are still on the waitlist. Once your request has been approved, we will send you an email notification. Thank you for your patience.'
        );
      } else {
        setError(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(endpoint, payload, { timeout: 15000 });

      if (response.data?.access_token) {
        onLogin(response.data.access_token, response.data.user);
      } else {
        setError(
          'Sign-in did not return a session. If you are running a production build locally, use `npm start` or rebuild with REACT_APP_API_URL=http://localhost:3001.'
        );
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === 'waitlist_pending') {
        setError(
          'You are still on the waitlist. Once your request has been approved, we will send you an email notification. Thank you for your patience.'
        );
      } else {
        setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFormLayout
      headline={isLogin ? 'Welcome back' : 'Create your account'}
      subhead="Connect your finances to your future. A calm place to plan."
    >
      <motion.div {...fadeIn}>
        {error && (
          <div
            className={`mb-4 p-4 rounded-md border text-body ${
              error.includes('waitlist')
                ? 'border-warning bg-bone-raised text-warning'
                : 'border-danger bg-bone-raised text-danger'
            }`}
            role="alert"
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {!isLogin && (
            <div>
              <label htmlFor="full_name" className="block text-label text-secondary mb-2">
                Full name
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                className="input-editorial"
                placeholder="Your name"
                required={!isLogin}
                autoComplete="name"
              />
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-label text-secondary mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="input-editorial"
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-label text-secondary mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="input-editorial"
              placeholder="Enter your password"
              required
              autoComplete={isLogin ? 'current-password' : 'new-password'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-bone border-t-transparent rounded-full animate-spin" />
                {isLogin ? 'Signing in…' : 'Creating account…'}
              </span>
            ) : isLogin ? (
              'Sign in'
            ) : (
              'Create account'
            )}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google sign-in failed. Please try again.')}
            theme="outline"
            shape="rectangular"
            size="large"
            text={isLogin ? 'signin_with' : 'signup_with'}
            width="100%"
          />
        </div>

        {isLogin && (
          <p className="text-center">
            <Link
              to="/forgot-password"
              className="text-body text-pine hover:text-pine-deep transition-colors duration-300"
            >
              Forgot your password?
            </Link>
          </p>
        )}

        <p className="text-center text-body">
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setFormData({ email: '', password: '', full_name: '' });
            }}
            className="text-pine hover:text-pine-deep transition-colors duration-300"
          >
            {isLogin ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
          </button>
        </p>
      </motion.div>
    </AuthFormLayout>
  );
};

export default AuthScreen;
